import dayjs from 'dayjs';

/**
 * Function to convert a date to "yyyy-MM-DD hh:mm:ss" format.
 * Accepts either user-entered "MM/DD/yyyy" values (e.g. from a date picker) or values already
 * normalized to "yyyy-MM-DD hh:mm:ss" (e.g. populated directly from a form-template/search response).
 * The time portion is intentionally discarded because these fields represent calendar dates.
 */
export function getISODate(date?: string): string {
  if (!date || date.trim() === '') {
    return '';
  }

  const trimmed = date.trim();

  // Already normalized "YYYY-MM-DD..." (e.g. from a form-template/search response) - keep the calendar date as-is.
  if (/^\d{4}-\d{2}-\d{2}(?:$|[T ])/.test(trimmed)) {
    return `${trimmed.slice(0, 10)} 00:00:00`;
  }

  // User-entered "MM/DD/YYYY". Reassemble by string parts so we never build a Date (which would shift
  // across timezones when converted to UTC).
  const [month, day, year] = trimmed.split('/');
  if (!year || !month || !day) {
    return '';
  }

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} 00:00:00`;
}

export function getTodayISODate() {
  return dayjs().format('YYYY-MM-DD');
}

export function getTodayISODateTime(): string {
  return `${dayjs().format('YYYY-MM-DD')} 00:00:00`;
}

export function millisToMinutesAndSeconds(millis: number) {
  const minutes = Math.floor(millis / 60000);
  const seconds = Number(((millis % 60000) / 1000).toFixed(0));

  // 2:60 should be 3:00
  if (seconds === 60) {
    return {
      minutes: minutes + 1 < 10 ? `0${minutes + 1}` : (minutes + 1).toString(),
      seconds: '00',
    };
  }

  return {
    minutes: minutes < 10 ? `0${minutes}` : minutes.toString(),
    seconds: seconds < 10 ? `0${seconds}` : seconds.toString(),
  };
}

/**
 * Function to check if a given date has passed (is in the past).
 * @param date - Date string, Date object, or dayjs-compatible format
 * @returns true if the date is in the past, false otherwise
 */
export function hasDatePassed(date: string | Date | dayjs.Dayjs | undefined | null): boolean {
  if (!date) {
    return false;
  }

  const targetDate = dayjs(date);

  if (!targetDate.isValid()) {
    return false;
  }

  return targetDate.isBefore(dayjs());
}
